import { useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Minus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, StatCard, Loading } from "@/components/common";
import { FormDialog } from "@/components/FormDialog";
import { api, useFetch, errMsg, money, fmtDate } from "@/lib/api";
import { useAuth, useCurrency } from "@/context/AuthContext";

const CATS = {
  income: ["Rent", "Deposit retained", "Late fees", "Other income"],
  expense: ["Repairs & maintenance", "Mortgage interest", "Insurance", "Agent fees", "Utilities", "Council tax", "Ground rent & service charge", "Certificates & safety", "Legal & professional", "Other expense"],
};

export default function Finances() {
  const cur = useCurrency();
  const { session } = useAuth();
  const isAgent = session.business.type === "agent";
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(String(thisYear));
  const [landlord, setLandlord] = useState("all");
  const { data: sum, reload: reloadSum } = useFetch(`/biz/reports/summary?year=${year}${landlord !== "all" ? `&landlord_id=${landlord}` : ""}`);
  const { data: txs, reload } = useFetch("/biz/transactions");
  const { data: props } = useFetch("/biz/properties");
  const { data: landlords } = useFetch(isAgent ? "/biz/landlords" : null);
  const [type, setType] = useState(null);
  const refresh = () => { reload(); reloadSum(); };
  const del = async (x) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/biz/transactions/${x.id}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  const chart = (sum?.monthly || []).map((m) => ({ ...m, label: new Date(`${m.month}-01`).toLocaleDateString("en-GB", { month: "short" }) }));
  return (
    <div data-testid="finances-page">
      <PageHeader eyebrow="Accounts" title="Income & expenses" subtitle="Rent payments are added automatically. Log other income and costs here."
        actions={<>
          <Button variant="outline" onClick={() => setType("expense")} data-testid="add-expense-button"><Minus className="w-4 h-4 mr-1" />Add expense</Button>
          <Button onClick={() => setType("income")} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="add-income-button"><Plus className="w-4 h-4 mr-1" />Add income</Button>
        </>} />
      <div className="flex flex-wrap gap-3 mb-6">
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-32" data-testid="finance-year"><SelectValue /></SelectTrigger>
          <SelectContent>{[0, 1, 2, 3].map((i) => <SelectItem key={i} value={String(thisYear - i)}>{thisYear - i}</SelectItem>)}</SelectContent>
        </Select>
        {isAgent && (
          <Select value={landlord} onValueChange={setLandlord}>
            <SelectTrigger className="w-56" data-testid="finance-landlord"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All landlords</SelectItem>{(landlords || []).map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      {!sum ? <Loading /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Income" value={money(sum.income, cur)} tone="green" testid="fin-income" />
            <StatCard label="Expenses" value={money(sum.expense, cur)} tone="red" testid="fin-expense" />
            <StatCard label="Net" value={money(sum.net, cur)} testid="fin-net" />
            {landlord !== "all"
              ? <StatCard label={`Mgmt fee (${sum.fee_percent}%)`} value={money(sum.management_fee, cur)} hint={`Owed to landlord: ${money(sum.net_after_fee, cur)}`} tone="amber" testid="fin-fee" />
              : <StatCard label="Entries" value={(txs || []).length} testid="fin-count" />}
          </div>
          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="panel lg:col-span-2">
              <h3 className="text-lg font-bold mb-4">{year} by month</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} width={60} />
                    <Tooltip formatter={(v) => money(v, cur)} />
                    <Bar dataKey="income" name="Income" fill="#059669" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="expense" name="Expenses" fill="#DC2626" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="panel" data-testid="category-breakdown">
              <h3 className="text-lg font-bold mb-4">By category</h3>
              {sum.by_category.length === 0 ? <p className="text-sm text-slate-500">No entries this year.</p> : (
                <ul className="space-y-2 text-sm">
                  {sum.by_category.map((c) => (
                    <li key={c.type + c.category} className="flex justify-between">
                      <span>{c.category}</span><span className={`font-mono ${c.type === "income" ? "text-emerald-700" : "text-rose-600"}`}>{c.type === "expense" && "−"}{money(c.amount, cur)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
      <div className="panel p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead>Property</TableHead><TableHead className="text-right">Amount</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {(txs || []).map((x) => (
              <TableRow key={x.id} data-testid={`tx-row-${x.id}`}>
                <TableCell className="whitespace-nowrap">{fmtDate(x.date)}</TableCell>
                <TableCell>{x.category}</TableCell>
                <TableCell className="text-sm text-slate-600">{x.description}</TableCell>
                <TableCell className="text-sm">{x.property_name}</TableCell>
                <TableCell className={`text-right font-mono ${x.type === "income" ? "text-emerald-700" : "text-rose-600"}`}>{x.type === "expense" ? "−" : "+"}{money(x.amount, cur)}</TableCell>
                <TableCell className="text-right">{!x.rent_payment_id && <Button size="icon" variant="ghost" onClick={() => del(x)} data-testid={`tx-delete-${x.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {txs?.length === 0 && <p className="text-sm text-slate-500 p-6">No entries yet.</p>}
      </div>
      <FormDialog open={!!type} onOpenChange={(o) => !o && setType(null)} title={type === "income" ? "Add income" : "Add expense"} testid="tx-form"
        initial={{ date: new Date().toISOString().slice(0, 10) }}
        fields={[
          { name: "category", label: "Category", type: "select", required: true, options: (CATS[type] || []).map((c) => ({ value: c, label: c })), full: true },
          { name: "amount", label: "Amount", type: "number", required: true },
          { name: "date", label: "Date", type: "date", required: true },
          { name: "property_id", label: "Property", type: "select", nullable: true, noneLabel: "General / not linked", options: (props || []).map((p) => ({ value: p.id, label: p.name })), full: true },
          { name: "description", label: "Description", type: "textarea" },
        ]}
        onSubmit={async (v) => { await api.post("/biz/transactions", { ...v, type }); toast.success("Saved"); refresh(); }} />
    </div>
  );
}
